import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Grid, InputBase, Snackbar, Stack, Typography, styled } from '@mui/material';
import { useSnackbar } from 'notistack';
import Iconify from '../../../../components/Iconify';
import useLocales from '../../../../hooks/useLocales';

import useAuth from '../../../../hooks/useAuth';
import { useDispatch } from '../../../../redux/store';
import { openLoginSignup } from '../../../../redux/slices/authPages';
import palette from '../../../../theme/palette';
import { ThemeButtonPrimary, ThemeButtonSecondary } from '../../../GlobalStyles';
import Image from '../../../../components/Image';
import { useLangNavigate } from '../../../../hooks/useLangNavigate';

const StyledIconify = styled(Iconify)(({ theme }) => ({
  height: 24,
  width: 24,
  color: palette.visitor.icon,
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 300,
  color: '#383838',
  marginBottom: '.5rem',
}));

const InputContainer = styled(Stack)(({ theme }) => ({
  background: '#fff',
  height: 40,
  width: '100%',
  borderRadius: '.5rem',
  border: 2,
  padding: '0 16px',
  alignItems: 'center',
}));

const CustomInput = styled(InputBase)(({ theme }) => ({
  fontSize: 14,
  color: '#383838',
  fontWeight: 300,
  flex: 1,
  input: {
    '&::placeholder': {
      color: '#878787',
    },
  },
}));

const FooterText = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  fontFamily: 'sans-serif',
  fontWeight: 600,
  color: palette.visitor.dialog.otherText,
  cursor: 'pointer',
  marginBottom: 20,
}));

const _loginForm = {
  userUsername: '',
  userPassword: '',
  captcha: '',
};

const ErrorText = ({ children }) => (
  <span style={{ fontSize: '.75rem', color: palette.visitor.errorText }}>{children}</span>
);

const LinkText = ({ children }) => (
  <span style={{ fontWeight: 600, color: palette.visitor.link, textDecoration: 'underline' }}>{children}</span>
);

const Login = ({ setOpenForgotPasswordDialog, setActiveTab }) => {
  const { login } = useAuth();

  const dispatch = useDispatch();

  const { translate } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const navigate = useLangNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const [loginForm, setLoginForm] = useState(_loginForm);

  const [formError, setFormError] = useState({});

  const [statusMessage, setStatusMessage] = useState({
    open: false,
    message: '',
    type: 'success',
  });

  const [validationErrors, setValidationErrors] = useState(null);

  useEffect(() => {
    isValid(true);

    const keyDownHandler = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onSubmit();
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [loginForm]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((prevState) => ({ ...prevState, [name]: value }));
  };

  const onSubmit = async () => {
    if (!isValid()) return;

    setIsLoading(true);

    const requestData = {
      'user-username': loginForm?.userUsername.trim(),
      'user-password': loginForm?.userPassword.trim(),
    };

    try {
      await login(requestData);

      // close login/register dialog
      dispatch(openLoginSignup({ open: false, isLogin: true }));
    } catch (err) {
      console.log(`Error: ${err.message}`);
      const snackbarOptions = { variant: 'error', autoHideDuration: 5000 };
      enqueueSnackbar(err.message, snackbarOptions);
    } finally {
      setIsLoading(false);
    }
  };

  const isValid = (allowEmpty = false) => {
    let valid = true;
    const _error = {};

    if (!allowEmpty && loginForm?.userUsername.trim().length < 3) {
      valid = false;
      _error.userUsername = translate('invalid_x_must_be_atleast_y_characters', { x: translate('username'), y: '3' });
    } else if (allowEmpty && loginForm?.userUsername.trim() && loginForm?.userUsername.trim().length < 3) {
      valid = false;
      _error.userUsername = translate('invalid_x_must_be_atleast_y_characters', { x: translate('username'), y: '3' });
    } else {
      _error.userUsername = '';
    }

    if (!allowEmpty && loginForm?.userPassword.trim().length < 6) {
      valid = false;
      _error.userPassword = translate('invalid_x_must_be_atleast_y_characters', { x: translate('password'), y: '6' });
    } else if (allowEmpty && loginForm?.userPassword.trim() && loginForm?.userPassword.trim().length < 6) {
      valid = false;
      _error.userPassword = translate('invalid_x_must_be_atleast_y_characters', { x: translate('password'), y: '6' });
    } else {
      _error.userPassword = '';
    }

    // if (validateCaptcha(loginForm?.captcha?.trim(), false) === true) {
    //   _error.userCaptcha = '';
    // } else {
    //   valid = false;
    //   _error.userCaptcha = translate('captcha_not_match');
    // }

    setFormError((prevState) => _error);

    return valid;
  };

  return (
    <Grid container spacing={'10px'}>
      <Grid item md={5} sm={12} xs={12} sx={{ display: { md: 'block', sm: 'none', xs: 'none' } }}>
        <Image src={require('../../../../assets/visitor/visitor_banner.png')} />
      </Grid>
      <Grid item md={7} sm={12} xs={12}>
        <Box sx={{ maxWidth: '380px', mx: 'auto' }}>
          {validationErrors && (
            <Alert onClose={() => setValidationErrors(null)} severity="error" sx={{ width: '100%', my: '10px' }}>
              {
                <ul style={{ marginLeft: 10 }}>
                  {validationErrors?.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              }
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Label>{translate('enter_x', { x: translate('username') })}</Label>
            <InputContainer direction={'row'}>
              <CustomInput
                name="userUsername"
                value={loginForm?.userUsername}
                placeholder={translate('enter_x', { x: translate('username') })}
                onChange={(e) => handleFormChange(e)}
              />
            </InputContainer>
            {formError?.userUsername && <ErrorText>{formError?.userUsername}</ErrorText>}
          </Box>
          <Box>
            <Label>{translate('enter_x', { x: translate('password') })}</Label>
            <InputContainer direction={'row'}>
              <CustomInput
                type={showPassword ? 'text' : 'password'}
                name="userPassword"
                value={loginForm?.userPassword}
                placeholder={translate('enter_x', { x: translate('password') })}
                onChange={(e) => handleFormChange(e)}
              />
              <StyledIconify
                onClick={() => setShowPassword((prevState) => !prevState)}
                icon={showPassword ? 'ph:eye-light' : 'ph:eye-closed-light'}
                sx={{ color: '#545454', cursor: 'pointer' }}
              />
            </InputContainer>
            {formError?.userPassword && <ErrorText>{formError?.userPassword}</ErrorText>}
          </Box>
          {/* <Stack direction={'row'} justifyContent={'end'} mt={-1}>
            <ForgotPasswordText onClick={() => setOpenForgotPasswordDialog(true)}>
              {`${translate('forgot_password')}`}
            </ForgotPasswordText>
          </Stack> */}

          <Stack direction={'row'} spacing={'10px'} sx={{ mt: '24px', justifyContent: 'center' }}>
            <ThemeButtonPrimary onClick={() => onSubmit()} fullWidth variant="contained" loading={isLoading}>
              {translate('done')}
            </ThemeButtonPrimary>
            <ThemeButtonSecondary
              onClick={() => setOpenForgotPasswordDialog(true)}
              fullWidth
              variant="contained"
              loading={isLoading}
              sx={{ background: '#8D8D8D !important' }}
            >
              {translate('forgot_password')}
            </ThemeButtonSecondary>
          </Stack>

          <FooterText
            onClick={() => {
              dispatch(openLoginSignup({ open: false, isLogin: true }));
              navigate('/auth/register');
            }}
            sx={{
              mt: 2,
              textAlign: 'center',
              color: '#383838',
              fontWeight: 400,
              fontSize: '14px',
            }}
          >
            {translate('dont_have_an_account')} <LinkText>{translate('register_now')}</LinkText>
          </FooterText>
          {statusMessage?.open && (
            <Snackbar
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={statusMessage.open}
              autoHideDuration={3000}
              sx={{ width: 'fit-content' }}
              onClose={() => setStatusMessage((prevState) => ({ ...prevState, open: false }))}
            >
              <Alert
                onClose={() => setStatusMessage((prevState) => ({ ...prevState, open: false }))}
                severity={statusMessage.type}
                sx={{ width: '100%' }}
              >
                {statusMessage.message}
              </Alert>
            </Snackbar>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

Login.propTypes = {
  setOpenForgotPasswordDialog: PropTypes.func,
  setActiveTab: PropTypes.func,
};

export default Login;
